/**
 * DialogoSuMisura — dare a UNA azienda condizioni sue per uno strumento (atlante cap. 251, tappa 4; decisione di Simone
 * del 07/10/2026). Canone, quota compresa, prezzo dell'extra e una nota sul perché.
 *
 * Prima di confermare dice cosa succede: se l'azienda ha già lo strumento nell'abbonamento passa subito alle condizioni
 * nuove (il canone del mese si divide a giorni), oppure dopo il rinnovo se nel mese ha già extra maturato; se non ce
 * l'ha, le avrà quando lo attiva. Le regole di validità sono quelle del server (`erroreDelleCondizioni`).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import type { StrumentoACanone } from '@shared/strumenti/listino';
import { erroreDelleCondizioni } from '@shared/strumenti/modificaListino';
import { impostaCondizioniSuMisura, type EsitoSuMisura } from '../../services/strumentiService';
import { alUnita, condizioniDette, euro, euroUnita, unita } from './testiStrumenti';

const ERRORI = {
  canone_non_valido: 'Il canone va da 0 a 10.000 €, al centesimo.',
  quota_non_valida: 'La quota compresa è un numero intero fra 0 e 1.000.000.',
  extra_non_valido: 'Il prezzo dell’extra va da 0 a 100 €, con al massimo quattro decimali.',
} as const;

function numeroDa(testo: string): number {
  const t = testo.trim().replace(/\s/g, '').replace(',', '.');
  return t === '' ? Number.NaN : Number(t);
}
const testoDa = (n: number) => String(n).replace('.', ',');

export interface DialogoSuMisuraProps {
  aperto: boolean;
  onChiudi: () => void;
  onSalvato: (esito: EsitoSuMisura) => void;
  tenantId: string;
  nomeAzienda: string;
  /** Lo strumento del listino (le condizioni di oggi per tutti). */
  listino: StrumentoACanone;
  /** Le condizioni che l'azienda ha adesso (listino o su misura): da qui si parte. */
  attuali: StrumentoACanone;
  notaAttuale: string;
  /** Ha lo strumento nell'abbonamento: passa alle condizioni nuove subito (o al rinnovo). */
  nellAbbonamento: boolean;
}

export const DialogoSuMisura: React.FC<DialogoSuMisuraProps> = ({
  aperto, onChiudi, onSalvato, tenantId, nomeAzienda, listino, attuali, notaAttuale, nellAbbonamento,
}) => {
  const [canone, setCanone] = useState('');
  const [quota, setQuota] = useState('');
  const [extra, setExtra] = useState('');
  const [nota, setNota] = useState('');
  const [salvo, setSalvo] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /** Solo canone (`consumo: null`): su misura si tratta solo il canone. */
  const senzaConsumo = listino.consumo === null;

  useEffect(() => {
    if (!aperto) return;
    setCanone(testoDa(attuali.canone_mensile_eur));
    setQuota(attuali.consumo ? String(attuali.consumo.quota_compresa) : '0');
    setExtra(attuali.consumo ? testoDa(attuali.consumo.prezzo_extra_eur) : '0');
    setNota(notaAttuale);
    setErrore(null);
  }, [aperto, attuali, notaAttuale]);

  const u = listino.consumo?.unita ?? 'minuti';
  const valori = senzaConsumo
    ? { canone: numeroDa(canone), quota: 0, extra: 0 }
    : { canone: numeroDa(canone), quota: numeroDa(quota), extra: numeroDa(extra) };
  const erroreNumeri = erroreDelleCondizioni(valori.canone, valori.quota, valori.extra);
  const ugualeAlListino = !erroreNumeri && valori.canone === listino.canone_mensile_eur
    && valori.quota === (listino.consumo?.quota_compresa ?? 0) && valori.extra === (listino.consumo?.prezzo_extra_eur ?? 0);
  const notaMancante = nota.trim() === '';

  const conseguenze = useMemo(() => {
    if (erroreNumeri) return [];
    const r: string[] = senzaConsumo
      ? [`${nomeAzienda} pagherà ${euro(valori.canone)} al mese. Le altre aziende restano al listino.`, 'Su Stripe nasce un prezzo solo per questa azienda.']
      : [
        `${nomeAzienda} pagherà ${euro(valori.canone)} al mese, con ${valori.quota} ${unita(u, valori.quota)} compresi e poi ${euroUnita(valori.extra)} ${alUnita(u)}. Le altre aziende restano al listino.`,
        'Su Stripe nascono due prezzi solo per questa azienda.',
      ];
    r.push(nellAbbonamento
      ? 'Lo ha già nell’abbonamento: passa subito alle condizioni nuove e il canone del mese si divide a giorni. Se nel mese ha già speso in extra, il passaggio avviene subito dopo il rinnovo, per non perdere quell’extra.'
      : 'Non lo ha ancora nell’abbonamento: le condizioni varranno da quando lo attiva.');
    return r;
  }, [erroreNumeri, valori.canone, valori.quota, valori.extra, nomeAzienda, u, nellAbbonamento, senzaConsumo]);

  const salva = async () => {
    setSalvo(true);
    setErrore(null);
    try {
      onSalvato(await impostaCondizioniSuMisura({
        tenant_id: tenantId,
        strumento: listino.capability,
        condizioni: { canone_mensile_eur: valori.canone, quota_compresa: valori.quota, prezzo_extra_eur: valori.extra },
        nota: nota.trim(),
      }));
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvo(false);
    }
  };

  const campo = { size: 'small' as const, fullWidth: true };
  return (
    <Dialog open={aperto} onClose={salvo ? undefined : onChiudi} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>{listino.etichetta} su misura per {nomeAzienda}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.color.ash }}>
          Listino di oggi: {condizioniDette(listino)}. Tutti i prezzi sono IVA esclusa.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <TextField {...campo} label="Canone al mese" value={canone} onChange={(e) => setCanone(e.target.value)}
            inputProps={{ inputMode: 'decimal' }} InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
          {!senzaConsumo && (
            <>
              <TextField {...campo} label="Quota compresa" value={quota} onChange={(e) => setQuota(e.target.value)}
                inputProps={{ inputMode: 'numeric' }} InputProps={{ endAdornment: <InputAdornment position="end">{unita(u, 2)}</InputAdornment> }} />
              <TextField {...campo} label={`Extra ${alUnita(u)}`} value={extra} onChange={(e) => setExtra(e.target.value)}
                inputProps={{ inputMode: 'decimal' }} InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </>
          )}
        </Stack>
        <TextField sx={{ mt: 2 }} {...campo} label="Perché (resta scritto)" placeholder="Accordo con Luigi del 7 ottobre" value={nota} onChange={(e) => setNota(e.target.value)} />

        <Box sx={{ mt: 2, p: 2, bgcolor: tokens.color.bone, border: `1px solid ${tokens.color.line}`, borderRadius: `${tokens.radius.md}px` }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Cosa succede</Typography>
          {erroreNumeri
            ? <Typography variant="body2" sx={{ color: tokens.color.danger }}>{ERRORI[erroreNumeri]}</Typography>
            : ugualeAlListino
              ? <Typography variant="body2" sx={{ color: tokens.color.ash }}>Sono le condizioni del listino: per tornarci usa «Torna al listino».</Typography>
              : conseguenze.map((c) => <Typography key={c} variant="body2" sx={{ color: tokens.color.ash, mt: 0.5 }}>{c}</Typography>)}
          {!erroreNumeri && !ugualeAlListino && notaMancante && (
            <Typography variant="body2" sx={{ color: tokens.color.warning, mt: 0.5 }}>Scrivi il perché: fra qualche mese servirà a ricordarlo.</Typography>
          )}
        </Box>
        {errore && <Alert severity="error" sx={{ mt: 2 }}>{errore}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={salvo}>Annulla</Button>
        <Button variant="contained" disableElevation onClick={() => { void salva(); }}
          disabled={salvo || Boolean(erroreNumeri) || ugualeAlListino || notaMancante}
          startIcon={salvo ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Applica le condizioni
        </Button>
      </DialogActions>
    </Dialog>
  );
};
